import React, { useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import { useQuery } from '@apollo/react-hooks';

import Badge from '@atlaskit/badge';
import { token } from '@atlaskit/tokens';
import { N0 } from '@atlaskit/theme/colors';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';

import {
	useUnreadCommentsIsEnabled,
	useUnreadInlineComments,
} from './hooks/useUnreadInlineComments';
import { createUnreadCommentList } from './utils/unreadCommentCountUtils';
import { UnreadCommentQuery } from './UnreadCommentQuery.graphql';
import { GraphQLContentStatus } from './__types__/UnreadCommentQuery';
import type {
	UnreadCommentQuery as UnreadCommentQueryData,
	UnreadCommentQueryVariables,
	UnreadCommentQuery_comments_nodes as UnreadCommentNode,
	UnreadCommentQuery_comments_nodes_location_InlineComment as UnreadInlineCommentLocation,
} from './__types__/UnreadCommentQuery';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const BadgeContainer = styled.div<{ count: number }>((props) => ({
	color: token('color.text.inverse', N0),
	bottom: token('space.250', '20px'),
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
	left:
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		props.count > 9 ? token('space.075', '6px') : token('space.150', '12px'),
	position: 'absolute',
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DotContainer = styled.div({
	right: token('space.075', '6px'),
	position: 'absolute',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DotIcon = styled.span({
	height: token('space.075', '6px'),
	width: token('space.075', '6px'),
	backgroundColor: token('color.icon.brand', '#0052CC'),
	borderRadius: '50%',
	display: 'inline-block',
	border: `2px solid ${token('color.border.inverse', N0)}`,
});
export interface UnreadCommentCountProps {
	pageId: string;
	pageMode?: 'view' | 'edit';
}

const MAX_UNREAD_COMMENTS_SUPPORTED = 1000;

const UnreadCommentCountInner = ({ pageId, pageMode = 'view' }: UnreadCommentCountProps) => {
	const [
		{
			unreadCommentsListState,
			markerRefToCommentIdMap,
			shouldUpdateUnreadComments,
			shouldUpdateUnreadCommentsOnFirstRender,
			allInlineComments,
		},
		{
			updateUnreadCommentsListState,
			updateUnreadComments,
			updateUnreadCommentsOnFirstRender,
			updateReadCommentsListState,
			updateMarkerRefToCommentIdMap,
			updateAllInlineComments,
		},
	] = useUnreadInlineComments();
	const isUnreadCommentsEnabled = useUnreadCommentsIsEnabled();

	const { userId } = useSessionData();

	const isRenderer = pageMode === 'view';

	const { data, loading, error } = useQuery<UnreadCommentQueryData, UnreadCommentQueryVariables>(
		UnreadCommentQuery,
		{
			variables: {
				pageId,
				contentStatus: !isRenderer
					? [GraphQLContentStatus.CURRENT, GraphQLContentStatus.DRAFT]
					: [GraphQLContentStatus.CURRENT],
			},
			skip: !pageId || !isUnreadCommentsEnabled,
		},
	);

	useEffect(() => {
		//TODO: remove once https://product-fabric.atlassian.net/browse/COMMENTS-1131 is resolved
		if (pageId) {
			updateUnreadCommentsListState(() => []);
			updateAllInlineComments([]);
			updateUnreadCommentsOnFirstRender(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageId]);

	const resetStates = useCallback(() => {
		updateUnreadComments(false);
		updateUnreadCommentsOnFirstRender(false);
	}, [updateUnreadComments, updateUnreadCommentsOnFirstRender]);

	useEffect(() => {
		const commentNodes =
			(data?.comments?.nodes?.filter(
				(node) =>
					node !== null &&
					!(node.location as UnreadInlineCommentLocation).inlineResolveProperties
						?.resolvedByDangling,
			) as UnreadCommentNode[]) || ([] as UnreadCommentNode[]);

		if (commentNodes.length === 0) {
			return;
		}

		updateAllInlineComments(commentNodes);
	}, [data, loading, updateAllInlineComments]);

	useEffect(() => {
		if (allInlineComments.length === 0) {
			return;
		}

		if (
			userId &&
			((isRenderer && shouldUpdateUnreadComments) || shouldUpdateUnreadCommentsOnFirstRender)
		) {
			if (allInlineComments.length < MAX_UNREAD_COMMENTS_SUPPORTED) {
				void createUnreadCommentList({
					allInlineComments,
					updateUnreadCommentsListState,
					userId,
					pageId,
					updateReadCommentsListState,
					updateMarkerRefToCommentIdMap,
					markerRefToCommentIdMap,
					resetStates,
				});
			}
		}
	}, [
		pageId,
		pageMode,
		updateUnreadCommentsListState,
		userId,
		updateReadCommentsListState,
		updateMarkerRefToCommentIdMap,
		markerRefToCommentIdMap,
		shouldUpdateUnreadComments,
		shouldUpdateUnreadCommentsOnFirstRender,
		resetStates,
		allInlineComments,
		updateAllInlineComments,
		isRenderer,
	]);

	if (loading) {
		return null;
	}

	if (error) {
		markErrorAsHandled(error);
		resetStates();
		return null;
	}

	return isUnreadCommentsEnabled && unreadCommentsListState?.length > 0 ? (
		<>
			{expValEquals<'control' | 'dot' | 'count'>('unread_comments_navigation', 'cohort', 'dot') ? (
				<DotContainer>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<DotIcon className="unread-dot" data-testid="dot-variation" />
				</DotContainer>
			) : (
				<BadgeContainer count={unreadCommentsListState.length}>
					<Badge appearance="primary" max={9} testId="unread-comment-count">
						{unreadCommentsListState?.length}
					</Badge>
				</BadgeContainer>
			)}
		</>
	) : null;
};

export const UnreadCommentCount = withTransparentErrorBoundary({
	attribution: Attribution.COMMENTS,
})(UnreadCommentCountInner);
